import { isSignupSheetFiducialToken } from "./signupSheetLayout";
import { isSignupPaidLabel, optionalEventLabel } from "./signupSheetModel";
import { isPrintedBracketHeader } from "../../shared/signupSheets/printedTickColumns";

const TICK_MARKS = new Set([
  "x",
  "xx",
  "✓",
  "✔",
  "yes",
  "true",
  "checked",
  "1",
  "/",
  "\\",
  "v",
]);
const TICK_EMPTY = new Set(["", "false", "no", "off", "0", "none", "null"]);

const asTrimmedString = (value) =>
  typeof value === "string" ? value.trim() : "";

export const isTickMarkToken = (value) =>
  TICK_MARKS.has(String(value || "").trim().toLowerCase());

const isTickEmptyToken = (value) =>
  TICK_EMPTY.has(String(value || "").trim().toLowerCase());

const asStringList = (value) => {
  if (typeof value === "string") {
    return value
      .split(/[,;|/]+/)
      .map((item) => item.trim())
      .filter(Boolean);
  }
  if (Array.isArray(value)) {
    return value.map((item) => asTrimmedString(item)).filter((item) => item !== undefined);
  }
  return [];
};

const asTickTokens = (value) => {
  const tokens = [];
  asStringList(value).forEach((item) => {
    String(item)
      .split(/[\s,;|/]+/)
      .forEach((part) => {
        const token = part.trim();
        if (token) tokens.push(token);
      });
  });
  return tokens;
};

const marksToPrinted = (raw, printed) => {
  if (!printed.length) return null;
  if (raw.length === printed.length) {
    return printed.filter((_, index) => isTickMarkToken(raw[index]));
  }
  if (raw.length === printed.length + 1) {
    const withoutPaid = raw.slice(1);
    return printed.filter((_, index) => isTickMarkToken(withoutPaid[index]));
  }
  return null;
};

const ORDINAL_WORDS = {
  first: 1,
  second: 2,
  third: 3,
  fourth: 4,
  fifth: 5,
};

const ordinalColumnIndex = (token) => {
  const text = String(token || "").trim().toLowerCase();
  if (ORDINAL_WORDS[text]) return ORDINAL_WORDS[text] - 1;
  const match = text.match(/^([1-9]|10)(?:st|nd|rd|th)$/);
  if (!match) return null;
  return Number(match[1]) - 1;
};

const HEADER_STOP = /^(name|avg|average|lane|team|#|no|usbc|email|phone)$/i;
const EVENT_TICK_WORDS = new Set([
  "sgl",
  "sg",
  "single",
  "singles",
  "aeh",
  "aes",
  "hdcp",
  "hcp",
  "handicap",
  "scratch",
  "brackets",
  "bracket",
  "brk",
  "hg",
]);

export const nameTokensFromHints = (hints) => {
  const tokens = [];
  (Array.isArray(hints) ? hints : [hints]).forEach((hint) => {
    String(hint || "")
      .split(/\s+/)
      .forEach((part) => {
        const token = part.trim().toLowerCase();
        if (token.length >= 2) tokens.push(token);
      });
  });
  return tokens;
};

export const looksLikePersonNameTick = (token, nameHints = []) => {
  const text = String(token || "").trim();
  if (!text) return false;
  const lower = text.toLowerCase();
  if (EVENT_TICK_WORDS.has(lower)) return false;
  if (/^[A-Z]{2,5}$/.test(text)) return false;
  if (nameTokensFromHints(nameHints).includes(lower)) return true;
  return /^[A-Z][a-z]{2,}$/.test(text);
};

export const isPrintedTickHeader = (value) => {
  const text = String(value || "").trim();
  if (!text || text.length > 18) return false;
  if (isSignupSheetFiducialToken(text) || isSignupPaidLabel(text)) return false;
  if (HEADER_STOP.test(text)) return false;
  if (isPrintedBracketHeader(text)) return false;
  if (ordinalColumnIndex(text) != null) return false;
  if (/^\d+$/.test(text)) return false;
  if (!/[a-zA-Z]/.test(text)) return false;
  const lower = text.toLowerCase();
  if (EVENT_TICK_WORDS.has(lower)) return true;
  if (/^[A-Z]{2,5}$/.test(text)) return true;
  if (/^[A-Z][a-z]+$/.test(text)) return false;
  return true;
};

const printedLabels = (tickLabels) =>
  (tickLabels || [])
    .map((label) => String(label || "").trim())
    .filter((label) => isPrintedTickHeader(label));

const ordinalsToPrinted = (tokens, printed) => {
  if (!printed.length) return null;
  const mapped = [];
  let sawOrdinal = false;
  tokens.forEach((token) => {
    const index = ordinalColumnIndex(token);
    if (index == null) {
      if (
        token &&
        !mapped.some((label) => label.toLowerCase() === String(token).toLowerCase())
      ) {
        mapped.push(token);
      }
      return;
    }
    sawOrdinal = true;
    const label = printed[index];
    if (
      label &&
      !mapped.some((item) => item.toLowerCase() === label.toLowerCase())
    ) {
      mapped.push(label);
    }
  });
  return sawOrdinal ? mapped : null;
};

const printedMatch = (token, printed) =>
  printed.find(
    (label) => label.toLowerCase() === String(token || "").trim().toLowerCase()
  ) || null;

export const asTickList = (value, tickLabels = [], nameHints = [], fallbackLabels = []) => {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return Object.entries(value)
      .filter(([, marked]) => {
        if (marked === true) return true;
        return isTickMarkToken(asTrimmedString(marked));
      })
      .map(([label]) => label.trim())
      .filter(
        (label) =>
          label &&
          !isSignupSheetFiducialToken(label) &&
          !isSignupPaidLabel(label) &&
          !looksLikePersonNameTick(label, nameHints)
      );
  }

  const printed = printedLabels(tickLabels);
  const raw = Array.isArray(value)
    ? value.map((item) => asTrimmedString(item))
    : [];
  if (
    raw.length &&
    raw.every((token) => isTickMarkToken(token) || isTickEmptyToken(token))
  ) {
    const mapped = marksToPrinted(raw, printed);
    if (mapped) return mapped;
    if (raw.filter((token) => isTickMarkToken(token)).length === 1 && printed[0]) {
      return [printed[0]];
    }
  }

  const tokens = asTickTokens(value);
  if (
    printed.length &&
    tokens.length === printed.length &&
    tokens.every((token) => isTickMarkToken(token) || isTickEmptyToken(token))
  ) {
    return printed.filter((_, index) => isTickMarkToken(tokens[index]));
  }

  const named = tokens.filter(
    (token) =>
      !isTickMarkToken(token) &&
      !isTickEmptyToken(token) &&
      !isSignupSheetFiducialToken(token) &&
      !isSignupPaidLabel(token)
  );
  if (named.length) {
    const fromOrdinals = ordinalsToPrinted(
      named,
      printed.length ? printed : printedLabels(fallbackLabels)
    );
    if (fromOrdinals) return fromOrdinals;
    const eventNamed = [];
    let personNamed = 0;
    named.forEach((token) => {
      const matched = printedMatch(token, printed);
      if (matched) {
        if (
          !eventNamed.some(
            (label) => label.toLowerCase() === matched.toLowerCase()
          )
        ) {
          eventNamed.push(matched);
        }
        return;
      }
      if (looksLikePersonNameTick(token, nameHints)) {
        personNamed += 1;
        return;
      }
      if (isPrintedTickHeader(token)) {
        eventNamed.push(token);
      }
    });
    if (eventNamed.length) return eventNamed;
    if (personNamed && printed[0]) return [printed[0]];
    return [];
  }
  if (
    printed[0] &&
    tokens.some((token) => isTickMarkToken(token)) &&
    tokens.every(
      (token) => isTickMarkToken(token) || isTickEmptyToken(token)
    )
  ) {
    return [printed[0]];
  }
  return named;
};

const isTickSizedCell = (value) => {
  const text = asTrimmedString(value);
  if (!text) return true;
  if (isTickMarkToken(text)) return true;
  if (/^\d{2,3}$/.test(text)) return false;
  if (/@/.test(text) || /^\d{4,}$/.test(text)) return false;
  if (/[a-zA-Z]{3,}/.test(text) && text.length > 4) return false;
  return text.length <= 3;
};

export const ticksFromSignupTableCells = (cells, tickLabels = []) => {
  const printed = printedLabels(tickLabels);
  if (!printed.length || !Array.isArray(cells) || !cells.length) return [];
  const right = [];
  for (let index = cells.length - 1; index >= 0 && right.length < printed.length; index -= 1) {
    const text = asTrimmedString(cells[index]);
    if (text && !isTickSizedCell(text)) break;
    right.unshift(text);
  }
  if (!right.length) return [];
  return printed
    .slice(0, right.length)
    .filter((_, index) => isTickMarkToken(right[index]));
};

export const signupTickLabelsForEvents = (events, singlesEvent) => {
  const labels = [];
  const add = (event) => {
    const label = optionalEventLabel(event);
    if (!label) return;
    if (labels.some((item) => item.toLowerCase() === label.toLowerCase())) {
      return;
    }
    labels.push(label);
  };
  if (singlesEvent) add(singlesEvent);
  (events || []).forEach((event) => {
    if (event?.isPrimary) return;
    add(event);
  });
  return labels;
};

export const mergeSignupTickLabels = ({ storedLabels } = {}) => {
  const stored = [];
  (storedLabels || []).forEach((label) => {
    const text = String(label || "").trim();
    if (!isPrintedTickHeader(text)) return;
    if (stored.some((item) => item.toLowerCase() === text.toLowerCase())) return;
    stored.push(text);
  });
  return stored;
};

const ticksSource = (row) => {
  if (Array.isArray(row?.ticks) && row.ticks.length) return row.ticks;
  if (row?.ticks && !Array.isArray(row.ticks)) return row.ticks;
  if (Array.isArray(row?.ticksRaw) && row.ticksRaw.length) return row.ticksRaw;
  return row?.ticks;
};

export const withNormalizedSignupTicks = (
  row,
  tickLabels,
  nameHints = [],
  fallbackLabels = []
) => ({
  ...row,
  ticksRaw: row?.ticksRaw ?? row?.ticks,
  ticks: asTickList(
    ticksSource(row),
    tickLabels,
    [...nameHints, row?.name],
    fallbackLabels
  ),
});
